// version a tester
import React, { useState, useCallback, useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router";
import { useParams } from "react-router";

import OT from '@opentok/client';

import { apiClient }      from "../../actions/api"
import { setFinishedCall} from "../../actions/mixedmode";

import ModernHairCheck from '../../components/vonage/HairCheck/ModernHairCheck'
import VideoCall      from '../../components/vonage/VideoCall/VideoCall'

const Page = () => {
  const { id, refresh } = useParams()
  const { user } = useSelector(state => state.user)
  const history  = useHistory()
  const dispatch = useDispatch()

  const [vonageConfig, setVonageConfig] = useState(null)
  const [session, setSession] = useState(null)
  const [publisher, setPublisher] = useState(null)
  const [subscribers, setSubscribers] = useState([])
  const [inRoom, setInRoom] = useState(false)
  const [patients, setPatients] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  
  const subscribersContainer = useRef({});

  // Refs pour le cleanup au démontage — évite que le useEffect cleanup
  // se déclenche à chaque setState (ce qui déconnecterait la session active).
  const sessionRef    = useRef(null);
  const publisherRef  = useRef(null);
  const subscribersRef = useRef([]);

  const VONAGE_API_KEY = '47748171';

  useEffect(() => {
    if (refresh) {
      window.location = window.location.origin + "/room-call/" + id
    }
    else if (user.google_id === "V") {
      let params = {
        token: user.api_token,
        api_token: user.api_token,
      }

      apiClient().post(`/getconsultation/therapeut`, params)
      .then((res) => {
        if (res.data.success === 'ok' && res.data.reservation?.heure_rdv) {
          setPatients(res.data.patients)
        }
      })
    }
  }, [])

  useEffect(() => {
    initVonageConnection();
  }, [])

  const initVonageConnection = async () => {
    try {
      console.log("=== DÉBUT CONNEXION VONAGE ===");
      
      const sessionResponse = await getSessionId();
      if (!sessionResponse.success) {
        console.error("Échec récupération sessionId");
        history.replace("/");
        return;
      }

      const sessionId = sessionResponse.sessionId;
      console.log("SessionId récupéré:", sessionId);

      const tokenResponse = await getConnectionToken(sessionId);
      if (!tokenResponse.success) {
        console.error("Échec génération token");
        history.replace("/");
        return;
      }

      const connectionToken = tokenResponse.token;
      console.log("Token de connexion généré");

      const config = {
        apiKey: VONAGE_API_KEY,
        sessionId: sessionId,
        token: connectionToken
      };

      console.log("Configuration Vonage prête");
      setVonageConfig(config);
      
      await createVonageSession(config);

    } catch (error) {
      console.error("Erreur initialisation Vonage:", error);
      history.replace("/");
    }
  }

  const getSessionId = async () => {
    try {
      let params = {
        token: user.api_token,
        api_token: user.api_token,
        id: id
      }

      const response = await apiClient().post(`/roomtoken`, params);
      console.log("Réponse /roomtoken:", response.data);

      if (response.data.success === 'ok' && response.data.room) {
        return {
          success: true,
          sessionId: response.data.room
        };
      }

      return { success: false };
    } catch (error) {
      console.error("Erreur /roomtoken:", error);
      return { success: false };
    }
  }

  const getConnectionToken = async (sessionId) => {
    try {
      let params = {
        token: user.api_token,
        api_token: user.api_token,
        sessionId: sessionId
      }

      const response = await apiClient().post(`/tokenforsession`, params);
      console.log("Réponse /tokenforsession:", response.data);

      if (response.data.success === 'ok' && response.data.token) {
        return {
          success: true,
          token: response.data.token
        };
      }

      return { success: false };
    } catch (error) {
      console.error("Erreur /tokenforsession:", error);
      return { success: false };
    }
  }

  const createVonageSession = async (config) => {
    try {
      console.log("Création session Vonage...");
      
      const newSession = OT.initSession(config.apiKey, config.sessionId);

      newSession.on('streamCreated', (event) => {
        console.log("Nouveau participant:", {
          streamId: event.stream.id,
          name: event.stream.name,
          hasVideo: event.stream.hasVideo,
          hasAudio: event.stream.hasAudio
        });
        
        const subscriber = newSession.subscribe(event.stream, null, {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          subscribeToAudio: true,
          subscribeToVideo: true
        }, (subscribeError) => {
          if (subscribeError) {
            console.error("Erreur subscription:", subscribeError);
          } else {
            console.log("Subscription réussie pour:", event.stream.id);
          }
        });

        setSubscribers(prev => {
          const newSubs = [...prev, { 
            subscriber, 
            streamId: event.stream.id,
            isScreenShare: event.stream.name?.includes('Screen Share')
          }];
          console.log("Subscribers mis à jour:", newSubs.length);
          return newSubs;
        });
      });

      newSession.on('streamDestroyed', (event) => {
        console.log("Participant parti:", event.stream.id);
        setSubscribers(prev => {
          const filtered = prev.filter(sub => sub.streamId !== event.stream.id);
          console.log("Subscribers après départ:", filtered.length);
          return filtered;
        });
      });

      newSession.on('sessionConnected', (event) => {
        console.log("Session connectée:", event);
        console.log("Création publisher pour l'appel...");
        
        let deviceSettings = null;
        try {
          const savedSettings = sessionStorage.getItem('vonageDeviceSettings');
          if (savedSettings) {
            deviceSettings = JSON.parse(savedSettings);
            console.log("Settings devices récupérés:", deviceSettings);
          }
        } catch (error) {
          console.log("Pas de settings devices sauvegardés");
        }

        const publisherOptions = {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          name: user.name || 'Utilisateur',
          publishAudio: true,
          publishVideo: true,
          style: {
            buttonDisplayMode: 'off'
          }
        };

        if (deviceSettings) {
          if (deviceSettings.selectedCamera) {
            publisherOptions.videoSource = deviceSettings.selectedCamera;
          }
          if (deviceSettings.selectedMicrophone) {
            publisherOptions.audioSource = deviceSettings.selectedMicrophone;
          }
        }

        const pub = OT.initPublisher(null, publisherOptions, (initError) => {
          if (initError) {
            console.error("Erreur initialisation publisher:", initError);
            return;
          }
          
          console.log("Publisher créé, publication en cours...");
          
          newSession.publish(pub, (publishError) => {
            if (publishError) {
              console.error("Erreur publication:", publishError);
              alert("Erreur de publication : " + publishError.message);
            } else {
              console.log("Publisher publié avec succès");
              setPublisher(pub);
              setInRoom(true);
              
              sessionStorage.removeItem('vonageDeviceSettings');
            }
          });
        });
      });

      newSession.on('sessionDisconnected', (event) => {
        console.log("Session déconnectée:", event.reason);
        setInRoom(false);
        setSession(null);
        setPublisher(null);
        setSubscribers([]);
      });

      newSession.on('connectionCreated', (event) => {
        console.log("Nouvelle connexion:", event.connection.connectionId);
      });

      newSession.on('connectionDestroyed', (event) => {
        console.log("Connexion fermée:", event.connection.connectionId);
      });

      setSession(newSession);
      console.log("Session créée et configurée");

    } catch (error) {
      console.error("Erreur création session:", error);
      throw error;
    }
  }

  const joinCall = useCallback((deviceSettings = null) => {
    if (session && vonageConfig && !isConnecting) {
      console.log("Connexion à la session...");
      setIsConnecting(true);
      
      if (deviceSettings) {
        console.log("Sauvegarde des settings devices:", deviceSettings);
        sessionStorage.setItem('vonageDeviceSettings', JSON.stringify(deviceSettings));
      }
      
      session.connect(vonageConfig.token, (error) => {
        setIsConnecting(false);
        
        if (error) {
          console.error("Erreur connexion session:", error);
          alert("Erreur de connexion : " + error.message);
        } else {
          console.log("Connecté à la session Vonage !");
        }
      });
    } else {
      console.log("Session ou config manquante pour connexion");
    }
  }, [session, vonageConfig, isConnecting]);

  const therapistEnd = () => {
    let params2 = {
      token: user.api_token,
      api_token: user.api_token,
      patients: patients,
      action: 'valide'
    }

    apiClient().post(`/set/reservation`, params2)
      .then((res) => { history.push('/fin-consultation/therapeut/' + id) })
      .catch(e => { history.push('/fin-consultation/therapeut/' + id) })
  }

  const patientEnd = () => {
    history.push('/fin-consultation/patient/' + id)
  }

  // ✅ FONCTION HELPER : Vérifier si tous les tracks sont arrêtés
  const waitForTracksToStop = () => {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20; // 20 tentatives × 100ms = 2 secondes max
      
      const checkTracks = () => {
        attempts++;
        
        // Vérifier s'il reste des éléments <video> avec srcObject actif
        const videoElements = document.querySelectorAll('video');
        const activeVideos = Array.from(videoElements).filter(v => v.srcObject !== null);
        
        console.log(`[Tentative ${attempts}/${maxAttempts}] Vidéos actives: ${activeVideos.length}`);
        
        if (activeVideos.length === 0) {
          console.log("✅ Tous les tracks sont arrêtés !");
          resolve(true);
        } else if (attempts >= maxAttempts) {
          console.warn("⚠️ Timeout - Force la redirection malgré les tracks actifs");
          resolve(false);
        } else {
          // Réessayer dans 100ms
          setTimeout(checkTracks, 100);
        }
      };
      
      checkTracks();
    });
  };

  const startLeavingCall = async () => {
    // Protection contre les double-clicks
    if (isLeaving) {
      console.log("Déjà en cours de déconnexion...");
      return;
    }
    
    setIsLeaving(true);
    console.log("=== DÉBUT FERMETURE SESSION ===");
    
    // 1. Arrêter tous les tracks média du publisher
    if (publisher) {
      console.log("Arrêt du publisher...");
      try {
        const videoTrack = publisher.getVideoSource();
        const audioTrack = publisher.getAudioSource();
        
        if (videoTrack && typeof videoTrack.stop === 'function') {
          videoTrack.stop();
          console.log("✓ Track vidéo arrêté");
        }
        
        if (audioTrack && typeof audioTrack.stop === 'function') {
          audioTrack.stop();
          console.log("✓ Track audio arrêté");
        }
      } catch (error) {
        console.error("Erreur arrêt des tracks publisher:", error);
      }
      
      // Unpublish depuis la session
      if (session) {
        try {
          session.unpublish(publisher);
          console.log("✓ Publisher unpublished");
        } catch (error) {
          console.error("Erreur unpublish:", error);
        }
      }
      
      // Détruire le publisher
      try {
        publisher.destroy();
        console.log("✓ Publisher détruit");
      } catch (error) {
        console.error("Erreur destruction publisher:", error);
      }
    }
    
    // 2. Arrêter tous les subscribers
    if (subscribers.length > 0) {
      console.log(`Arrêt de ${subscribers.length} subscriber(s)...`);
      subscribers.forEach(sub => {
        if (sub.subscriber) {
          try {
            sub.subscriber.destroy();
            console.log("✓ Subscriber détruit:", sub.streamId);
          } catch (error) {
            console.log("Erreur destruction subscriber:", error);
          }
        }
      });
    }
    
    // 3. Déconnecter la session
    if (session) {
      try {
        session.disconnect();
        console.log("✓ Session déconnectée");
      } catch (error) {
        console.error("Erreur déconnexion session:", error);
      }
    }

    // 4. Nettoyer tous les éléments <video> du DOM
    console.log("Nettoyage des éléments vidéo du DOM...");
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      try {
        if (video.srcObject) {
          const tracks = video.srcObject.getTracks();
          tracks.forEach(track => {
            track.stop();
            console.log(`✓ Track ${track.kind} arrêté depuis DOM`);
          });
          video.srcObject = null;
        }
        video.remove();
        console.log("✓ Élément <video> supprimé du DOM");
      } catch (error) {
        console.log("Erreur nettoyage video DOM:", error);
      }
    });

    // 5. Notifier le backend
    let paramsDispatch = {
      token: user.api_token,
      api_token: user.api_token,
      id: id
    }
    dispatch(setFinishedCall(paramsDispatch))

    // ✅ 6. ATTENDRE que tous les tracks soient vraiment arrêtés
    console.log("⏳ Attente de l'arrêt complet des tracks...");
    await waitForTracksToStop();
    
    // ✅ 7. Sécurité : Flag pour indiquer à fin-consultation de vérifier
    sessionStorage.setItem('needsMediaCleanup', 'true');
    
    // 8. MAINTENANT on peut rediriger
    console.log("=== REDIRECTION VERS FIN CONSULTATION ===");
    if (user.google_id === "V") {
      therapistEnd()
    } else {
      patientEnd()
    }
  }

  // Sync des refs avec le state — les refs sont lues dans le cleanup
  // sans jamais déclencher un nouveau cycle d'effet.
  useEffect(() => { sessionRef.current    = session;    }, [session]);
  useEffect(() => { publisherRef.current  = publisher;  }, [publisher]);
  useEffect(() => { subscribersRef.current = subscribers; }, [subscribers]);

  // Cleanup au démontage du composant UNIQUEMENT (deps vides).
  // IMPORTANT : ne PAS mettre [publisher, subscribers, session] ici —
  // cela exécuterait le return/cleanup à chaque setState et déconnecterait
  // la session Vonage active, provoquant la boucle de reconnexion.
  useEffect(() => {
    return () => {
      console.log("Démontage du composant - cleanup...");

      const pub  = publisherRef.current;
      const subs = subscribersRef.current;
      const sess = sessionRef.current;

      if (pub) {
        try {
          const videoTrack = pub.getVideoSource();
          const audioTrack = pub.getAudioSource();

          if (videoTrack && typeof videoTrack.stop === 'function') {
            videoTrack.stop();
          }
          if (audioTrack && typeof audioTrack.stop === 'function') {
            audioTrack.stop();
          }

          pub.destroy();
        } catch (error) {
          console.log("Erreur cleanup publisher au démontage:", error);
        }
      }

      subs.forEach(sub => {
        if (sub.subscriber) {
          try {
            sub.subscriber.destroy();
          } catch (error) {
            console.log("Erreur cleanup subscriber au démontage:", error);
          }
        }
      });

      if (sess) {
        try {
          sess.disconnect();
        } catch (error) {
          console.log("Erreur cleanup session au démontage:", error);
        }
      }
    };
  }, []); // deps vides = s'exécute uniquement au vrai unmount du composant

  // Pendant la phase de déconnexion, aucun composant vidéo ne doit être monté.
  // Sans ce guard, les setState non-batch de sessionDisconnected (React 17)
  // provoquent un rendu intermédiaire où ModernHairCheck se remonte brièvement,
  // ouvre un nouveau getUserMedia et laisse des tracks caméra/micro actifs.
  if (isLeaving) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Fin de la consultation...</p>
      </div>
    );
  }

  if (inRoom && session && publisher) {
    return (
      <div className="home vonage">
        <VideoCall
          session={session}
          publisher={publisher}
          subscribers={subscribers}
          vonageConfig={vonageConfig}
          onLeave={startLeavingCall}
        />
      </div>
    );
  }

  if (vonageConfig && session) {
    return (
      <ModernHairCheck
        joinCall={joinCall}
        cancelCall={startLeavingCall}
        vonageConfig={vonageConfig}
        session={session}
      />
    );
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <p>Initialisation de la visioconférence...</p>
      <p>Connexion au serveur Vonage...</p>
    </div>
  );
}

export default Page